import React from 'react';
import { Badge } from 'react-bootstrap';

/**
 * Display-id chip for tenant-scoped sequence numbers introduced by track
 * extend_spray_schema_20260430.
 *
 * - `prefix` is the entity-specific tag: 'PGM' for programs, 'R' for rounds,
 *   'SES' for sessions. Programs and sessions render as 'PGM-014';
 *   rounds render bare ('R3') because they're always seen in their parent
 *   program's context.
 * - `seq` is the sequence value. When `seq` is `undefined` (legacy entities
 *   created before this track shipped), the chip renders nothing — that
 *   absence is the visible legacy-state signal in the UI.
 *
 * Per spec §4 / S2: only entities created after the track shipped get a
 * sequence number; we do not backfill.
 */
interface Props {
  prefix: 'PGM' | 'R' | 'SES';
  seq?: number;
  className?: string;
}

const IdChip: React.FC<Props> = ({ prefix, seq, className }) => {
  if (seq == null) return null;
  // Bare for rounds; dash-padded for programs/sessions.
  const text = prefix === 'R' ? `R${seq}` : `${prefix}-${String(seq).padStart(3, '0')}`;
  return (
    <Badge
      bg="light"
      text="dark"
      pill
      className={`fw-normal text-muted border ms-2 ${className || ''}`}
      data-testid={`id-chip-${prefix.toLowerCase()}`}
    >
      {text}
    </Badge>
  );
};

export default IdChip;
